import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { CreditCard, Lock, ArrowLeft, Mail, CheckCircle } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Pro subscription form using CardElement
const ProSubscriptionForm = ({ 
  selectedPlan, 
  clientSecret, 
  setupIntentId,
  onSuccess, 
  onError,
  theme
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [processing, setProcessing] = useState(false);

  // Get user info from localStorage
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user.email) setEmail(user.email);
    if (user.name) setName(user.name);
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements || !clientSecret) {
      console.log('Missing stripe/elements/clientSecret');
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      onError('Payment form not ready. Please refresh and try again.');
      return;
    }

    setProcessing(true);

    try {
      console.log('Confirming setup with Stripe...');
      
      // Use CardElement for SetupIntent
      const { error, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: name,
            email: email,
          },
        }
      });

      if (error) {
        console.error('Setup error:', error);
        onError(error.message);
      } else if (setupIntent.status === 'succeeded') {
        console.log('Setup succeeded:', setupIntent.id);
        
        // Now create the pro subscription on our backend
        const response = await fetch('/api/stripe/confirm-pro-subscription', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            setup_intent_id: setupIntent.id
          })
        });

        if (response.ok) {
          const result = await response.json();
          console.log('Pro subscription created:', result);
          onSuccess(result);
        } else {
          const errorData = await response.json();
          onError(errorData.detail || 'Pro subscription creation failed');
        }
      }
    } catch (err) {
      console.error('Pro setup error:', err);
      onError(err.message || 'Pro setup failed');
    } finally {
      setProcessing(false);
    }
  };

  // CardElement styling
  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: theme.mainPanelText,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        backgroundColor: 'transparent',
        iconColor: theme.mainPanelText,
        '::placeholder': {
          color: theme.inputPlaceholder || '#999',
        },
      },
      invalid: {
        color: '#ef4444',
        iconColor: '#ef4444'
      },
    },
    hidePostalCode: false,
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Contact Information */}
      <div>
        <h3 
          className="text-lg font-medium mb-4"
          style={{ color: theme.mainPanelText }}
        >
          Contact information
        </h3>
        
        <div>
          <label 
            className="block text-sm font-medium mb-2"
            style={{ color: theme.mainPanelText }}
          >
            Email
          </label>
          <div className="relative">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
              className="w-full px-4 py-3 rounded-lg border transition-colors pr-12"
              style={{
                backgroundColor: theme.inputBg || theme.sidebarBg,
                borderColor: theme.inputBorder || '#ddd',
                color: theme.mainPanelText
              }}
            />
            <Mail 
              size={20} 
              className="absolute right-3 top-3 opacity-50"
              style={{ color: theme.mainPanelText }}
            />
          </div>
        </div>
      </div>

      {/* Payment Method */}
      <div>
        <h3 
          className="text-lg font-medium mb-4"
          style={{ color: theme.mainPanelText }}
        >
          Payment method
        </h3>

        <div className="space-y-4">
          {/* Card Element */}
          <div>
            <label 
              className="block text-sm font-medium mb-2"
              style={{ color: theme.mainPanelText }}
            >
              Card information
            </label>
            <div 
              className="p-4 rounded-lg border transition-colors"
              style={{
                backgroundColor: theme.inputBg || theme.sidebarBg,
                borderColor: theme.inputBorder || '#ddd'
              }}
            >
              <CardElement options={cardElementOptions} />
            </div>
          </div>

          {/* Cardholder Name */}
          <div>
            <label 
              className="block text-sm font-medium mb-2"
              style={{ color: theme.mainPanelText }}
            >
              Cardholder name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Full name on card"
              required
              className="w-full px-4 py-3 rounded-lg border transition-colors"
              style={{
                backgroundColor: theme.inputBg || theme.sidebarBg,
                borderColor: theme.inputBorder || '#ddd',
                color: theme.mainPanelText
              }}
            />
          </div>
        </div>
      </div>

      {/* Pro Benefits */}
      <div 
        className="p-4 rounded-lg border-l-4"
        style={{ 
          backgroundColor: theme.sidebarBg, 
          borderLeftColor: '#10b981',
          color: theme.mainPanelText 
        }}
      >
        <div className="flex items-start space-x-3">
          <CheckCircle size={20} className="text-green-500 mt-0.5" />
          <div>
            <p className="font-medium text-sm">Pro Features Included</p>
            <p className="text-sm opacity-75">
              500,000 AI tokens, advanced features, and priority support. 
              Billing starts immediately upon subscription.
            </p>
          </div>
        </div>
      </div>

      {/* Terms */}
      <div className="flex items-start space-x-3">
        <input
          type="checkbox"
          id="terms"
          required
          className="mt-1"
          style={{ accentColor: theme.buttonBg }}
        />
        <label 
          htmlFor="terms" 
          className="text-sm"
          style={{ color: theme.mainPanelText }}
        >
          I agree to subscribe to the Pro plan and understand that billing will begin immediately. 
          I can cancel anytime. I agree to the{' '}
          <a href="#" className="underline" style={{ color: theme.accentColor }}>
            Terms of Service
          </a>
          {' '}and{' '}
          <a href="#" className="underline" style={{ color: theme.accentColor }}>
            Privacy Policy
          </a>
          .
        </label>
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        disabled={!stripe || !elements || processing}
        className="w-full py-4 rounded-lg font-medium text-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        style={{
          backgroundColor: theme.buttonBg,
          color: theme.buttonText
        }}
      >
        {processing ? (
          <div className="flex items-center justify-center">
            <div 
              className="animate-spin rounded-full h-5 w-5 border-b-2 mr-3"
              style={{ borderColor: theme.buttonText }}
            ></div>
            Processing payment...
          </div>
        ) : (
          'Subscribe to Pro'
        )}
      </button>
    </form>
  );
};

// Main PaymentPage component
const PaymentPage = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isWelcome = searchParams.get('welcome') === 'true';
  
  const [selectedPlan, setSelectedPlan] = useState('monthly');
  const [clientSecret, setClientSecret] = useState('');
  const [setupIntentId, setSetupIntentId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Protect payment page - only allow proper access
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    
    if (!user.user_id || user.plan_status !== 'pending_pro' || !isWelcome) {
      console.log('Payment page access denied:', {
        authenticated: !!user.user_id,
        planStatus: user.plan_status,
        hasWelcomeParam: isWelcome
      });
      navigate('/dashboard');
      return;
    }
  }, [navigate, isWelcome]);

  // Create setup intent when component mounts or plan changes
  useEffect(() => {
    createProSetup();
  }, [selectedPlan]);

  const createProSetup = async () => {
    setLoading(true);
    setError('');
    setClientSecret('');

    try {
      console.log('Creating pro setup for plan:', selectedPlan);
      
      const response = await fetch('/api/stripe/create-pro-setup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          plan_type: selectedPlan
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create pro setup');
      }

      const { client_secret, setup_intent_id } = await response.json();
      console.log('Pro setup created successfully');
      
      setClientSecret(client_secret);
      setSetupIntentId(setup_intent_id);
    } catch (error) {
      console.error('Pro setup creation failed:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePlanChange = (plan) => {
    if (loading) return;
    console.log('Changing plan to:', plan);
    setSelectedPlan(plan);
  };

  const handleProSuccess = (result) => {
    console.log('Pro subscription successful, updating user and redirecting');
    
    // Update user in localStorage
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    user.plan_status = 'active_pro';
    localStorage.setItem('user', JSON.stringify(user));
    
    // Navigate to success page
    navigate('/payment-success?pro=activated');
  };

  const handleProError = (errorMessage) => {
    console.error('Pro subscription error:', errorMessage);
    setError(errorMessage);
  };

  const handleSkipForDev = () => {
    // Development bypass - remove in production
    localStorage.removeItem('pendingUser');
    navigate('/dashboard?payment=skipped');
  };

  // Define plan options for display
  const planOptions = {
    monthly: { 
      price: '$9.99/month', 
      label: 'Monthly',
      billingText: 'Billed monthly at $9.99'
    },
    yearly: { 
      price: '$99.99/year', 
      label: 'Yearly', 
      badge: '2 months free',
      billingText: 'Billed annually at $99.99'
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-6"
      style={{ backgroundColor: theme.mainPanelBg }}
    >
      <div className="max-w-2xl w-full">
        {/* Back Button */}
        <button
          onClick={() => navigate('/login')}
          className="flex items-center mb-6 text-sm hover:opacity-75 transition-opacity"
          style={{ color: theme.accentColor }}
        >
          <ArrowLeft size={16} className="mr-2" />
          Back to Login
        </button>

        {/* Header */}
        <div className="text-center mb-8">
          <CreditCard size={48} style={{ color: theme.buttonBg }} className="mx-auto mb-4" />
          <h1 
            className="text-3xl font-bold mb-2"
            style={{ color: theme.mainPanelText }}
          >
            Upgrade to Pro
          </h1>
          <p style={{ color: theme.mainPanelText }} className="opacity-80">
            Get instant access to pro features and 500,000 AI tokens.
          </p>
        </div>

        {/* Plan Selection */}
        <div className="mb-6">
          <div className="flex justify-center space-x-4">
            {Object.entries(planOptions).map(([planKey, planData]) => (
              <button
                key={planKey}
                onClick={() => handlePlanChange(planKey)}
                disabled={loading}
                className={`px-6 py-4 rounded-lg font-medium border-2 transition-all disabled:opacity-50 ${
                  selectedPlan === planKey ? 'border-2' : 'border-transparent'
                }`}
                style={{
                  backgroundColor: selectedPlan === planKey ? theme.buttonBg : theme.sidebarBg,
                  color: selectedPlan === planKey ? theme.buttonText : theme.mainPanelText,
                  borderColor: selectedPlan === planKey ? theme.buttonBg : theme.inputBorder
                }}
              >
                <div className="text-center">
                  <div className="font-bold">{planData.label}</div>
                  <div className="text-sm">{planData.billingText}</div>
                  {planData.badge && (
                    <div className="text-xs opacity-75 mt-1">{planData.badge}</div>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Development Notice */}
        <div 
          className="mb-6 p-4 rounded-lg border-l-4"
          style={{ 
            backgroundColor: theme.sidebarBg, 
            borderLeftColor: '#f59e0b',
            color: theme.mainPanelText 
          }}
        >
          <p className="text-sm font-medium">Development Mode</p>
          <p className="text-sm opacity-75">Using Stripe test mode - Use card: 4242424242424242</p>
          <button
            onClick={handleSkipForDev}
            className="mt-2 text-sm underline hover:opacity-75"
            style={{ color: theme.accentColor }}
          >
            Skip payment setup (dev only)
          </button>
        </div>

        {/* Pro Setup Form Container */}
        <div 
          className="rounded-lg shadow-lg overflow-hidden p-6"
          style={{ backgroundColor: theme.sidebarBg }}
        >
          {error && (
            <div className="p-4 bg-red-50 border-l-4 border-red-400 mb-6 rounded">
              <p className="text-red-700 text-sm">{error}</p>
              <button 
                onClick={() => setError('')}
                className="mt-2 text-xs text-red-600 hover:text-red-800"
              >
                Dismiss
              </button>
            </div>
          )}
          
          {loading && (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto mb-4"
                   style={{ borderColor: theme.buttonBg }}></div>
              <p style={{ color: theme.mainPanelText }}>Setting up your pro subscription...</p>
            </div>
          )}

          {clientSecret && (
            <Elements 
              stripe={stripePromise} 
              options={{ 
                clientSecret,
                appearance: {
                  theme: 'stripe'
                }
              }}
              key={clientSecret} // Force re-mount when clientSecret changes
            >
              <ProSubscriptionForm 
                selectedPlan={selectedPlan}
                clientSecret={clientSecret}
                setupIntentId={setupIntentId}
                onSuccess={handleProSuccess}
                onError={handleProError}
                theme={theme}
              />
            </Elements>
          )}
        </div>

        {/* Security Notice */}
        <div 
          className="mt-6 flex items-center justify-center text-sm opacity-70"
          style={{ color: theme.mainPanelText }}
        >
          <Lock size={16} className="mr-2" />
          Secured by Stripe - Your payment info is never stored on our servers
        </div>

        {/* Pro Details */}
        <div 
          className="mt-4 p-4 rounded-lg text-sm text-center"
          style={{ backgroundColor: theme.sidebarBg, color: theme.mainPanelText }}
        >
          <p className="opacity-80">
            <strong>Pro subscription includes:</strong> 500,000 AI tokens, advanced features, 
            priority support, and unlimited access to all pro tools. 
            Billing begins immediately upon subscription.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;